/**
 * One loan: what was agreed, what is owed, and every entry on its ledger.
 *
 * Corrections happen here and only as new entries: a reversal names the
 * payment it undoes, a cancellation reverses the disbursement, a write-off is
 * its own entry. Nothing on this screen deletes money that was recorded.
 */

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Ban, FileText, HandCoins, PenLine, Send, ShieldCheck, Undo2, XCircle } from 'lucide-react';
import {
  LOAN_COUNTERPARTY_LABELS,
  LOAN_EARLY_REPAYMENT_LABELS,
  LOAN_FEE_COLLECTION_LABELS,
  LOAN_DIRECTION_LABELS,
  LOAN_FREQUENCY_LABELS,
  LOAN_INTEREST_TYPE_LABELS,
  LOAN_REPAYMENT_SOURCE_LABELS,
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  addAmounts,
  compareAmounts,
  loanWording,
  moneySchema,
  type LoanDeductionSource,
  type LoanInstalmentView,
  type LoanTransactionView,
  type LoanView,
  type PaymentMethod,
} from '@cenox/shared';
import { ApiError, fetchBlob } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { money, dateTime, todayIn } from '../../lib/format';
import { Badge, Button, Card, CardHeader, ErrorState, Input, Modal, Select, Spinner, Textarea, cn } from '../../components/ui';
import {
  newSubmissionKey,
  useApplyLateFee,
  useRemoveLoanDocument,
  useSettleLoan,
  useSettlementQuote,
  useWaiveCharge,
  useAdjustLoan,
  useCancelLoan,
  useCreateAgreement,
  useTopUpLoan,
  useDeductionSources,
  useIssueLoan,
  useLoan,
  useRecordDeduction,
  useReverseTransaction,
  useRevokeAgreement,
  useUpdateLoan,
  useWriteOffLoan,
} from './data';
import { LoanStatusBadge, categoryName, day } from './parts';
import { RepaymentModal } from './RepaymentModal';
import { AccountSelect } from '../money/AccountSelect';

type Dialog = 'repay' | 'settle' | 'edit' | 'cancel' | 'writeoff' | 'agreement' | 'revoke' | 'deduct' | 'removeDoc' | 'topup' | 'adjust' | null;

export function LoanDetailPage(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const loan = useLoan(id);
  const [params, setParams] = useSearchParams();
  const routeLocation = useLocation();
  const [dialog, setDialog] = useState<Dialog>(null);

  useEffect(() => {
    if (params.get('edit') === '1' && loan.data) {
      setDialog('edit');
      setParams({}, { replace: true });
    }
  }, [params, loan.data, setParams]);

  useEffect(() => {
    if (routeLocation.hash === '#history' && loan.data) {
      document.getElementById('history')?.scrollIntoView({ block: 'start' });
    }
  }, [routeLocation.hash, loan.data]);

  if (loan.isPending) return <Spinner label="Loading loan" />;
  if (loan.isError) {
    return (
      <div className="space-y-4">
        <BackLink />
        <ErrorState
          message={loan.error instanceof ApiError && loan.error.status === 404 ? 'That loan was not found.' : 'Could not load the loan.'}
          onRetry={() => void loan.refetch()}
        />
      </div>
    );
  }

  return <LoanDetail loan={loan.data} dialog={dialog} setDialog={setDialog} />;
}

function BackLink(): ReactNode {
  return (
    <Link to="/loans" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
      <ArrowLeft className="size-4" aria-hidden /> Loans
    </Link>
  );
}

function LoanDetail({
  loan,
  dialog,
  setDialog,
}: {
  loan: LoanView;
  dialog: Dialog;
  setDialog: (d: Dialog) => void;
}): ReactNode {
  const { can } = useAuth();
  const issue = useIssueLoan(loan.id);
  const words = loanWording(loan.direction);
  const c = loan.currencyCode;
  const live = loan.status === 'active' || loan.status === 'overdue';
  const riderLoan = loan.direction === 'receivable' && loan.counterpartyKind === 'rider';

  return (
    <div className="space-y-5">
      <BackLink />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">{loan.reference}</h1>
            <LoanStatusBadge status={loan.status} />
            <Badge>{LOAN_DIRECTION_LABELS[loan.direction]}</Badge>
          </div>
          <p className="mt-1 text-sm text-slate-600">
            {loan.counterpartyName}
            {loan.counterpartyPhone ? ` · ${loan.counterpartyPhone}` : ''} · {categoryName(loan.category)} loan
            {loan.purpose ? ` · ${loan.purpose}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {loan.status === 'draft' && can('loans:create') && (
            <Button icon={<Send className="size-4" />} loading={issue.isPending} onClick={() => issue.mutate()}>
              Issue loan
            </Button>
          )}
          {live && can('loans:repay') && (
            <Button icon={<HandCoins className="size-4" />} onClick={() => setDialog('repay')}>
              {words.record}
            </Button>
          )}
          {live && can('loans:repay') && (
            <Button variant="secondary" onClick={() => setDialog('settle')}>Settle early</Button>
          )}
          {(live || loan.status === 'completed') && can('loans:create') && (
            <Button variant="secondary" onClick={() => setDialog('topup')}>
              {loan.direction === 'receivable' ? 'Add amount' : 'Add borrowing'}
            </Button>
          )}
          {live && can('loans:manage') && (
            <Button variant="secondary" onClick={() => setDialog('adjust')}>Correct balance</Button>
          )}
          {riderLoan && live && loan.deductionAgreement && can('loans:repay') && (
            <Button variant="secondary" onClick={() => setDialog('deduct')}>Record deduction</Button>
          )}
          {(can('loans:manage') || (loan.status === 'draft' && can('loans:create'))) && (
            <Button variant="secondary" icon={<PenLine className="size-4" />} onClick={() => setDialog('edit')}>
              Edit
            </Button>
          )}
          {(loan.status === 'draft' || live) && can('loans:manage') && (
            <Button variant="secondary" icon={<Ban className="size-4" />} onClick={() => setDialog('cancel')}>
              Cancel loan
            </Button>
          )}
          {live && loan.direction === 'receivable' && can('loans:write_off') && (
            <Button variant="danger" icon={<XCircle className="size-4" />} onClick={() => setDialog('writeoff')}>
              Write off
            </Button>
          )}
        </div>
      </div>
      {issue.error && <ErrorState message={issue.error.message} />}

      <section aria-label="Loan figures" className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Figure
          label="Original amount"
          value={money(loan.principal, c)}
          {...(compareAmounts(loan.additionalAmounts, 0) > 0 ? { sub: `+ ${money(loan.additionalAmounts, c)} added since` } : {})}
        />
        {compareAmounts(loan.additionalAmounts, 0) > 0 && (
          <Figure label="Additional amounts" value={money(loan.additionalAmounts, c)} sub="lent on this loan after issue" />
        )}
        {compareAmounts(loan.adjustments, 0) > 0 && (
          <Figure label="Balance corrections" value={money(loan.adjustments, c)} sub="no money moved; not repayments" />
        )}
        <Figure
          label="Total payable"
          value={money(loan.totalPayable, c)}
          sub={`${money(loan.totalInterest, c)} interest${compareAmounts(loan.totalFees, 0) > 0 ? ` · ${money(loan.totalFees, c)} fees` : ''}`}
        />
        <Figure label={loan.direction === 'receivable' ? 'Repaid' : 'Paid back'} value={money(loan.repaid, c)} sub={`${money(loan.principalRepaid, c)} principal · ${money(loan.interestRepaid, c)} interest`} tone="good" />
        <Figure label="Outstanding principal" value={money(loan.outstanding, c)} sub={`${money(loan.balanceOwed, c)} owed in all`} />
        {compareAmounts(loan.overdueAmount, 0) > 0 && <Figure label="Overdue" value={money(loan.overdueAmount, c)} tone="owed" />}
        {compareAmounts(loan.writtenOff, 0) > 0 && <Figure label="Written off" value={money(loan.writtenOff, c)} tone="warn" />}
        {compareAmounts(loan.interestRebated, 0) > 0 && (
          <Figure label="Interest waived" value={money(loan.interestRebated, c)} sub="early settlement" />
        )}
        {loan.nextDueOn && <Figure label="Next payment" value={money(loan.nextDueAmount, c)} sub={`due ${day(loan.nextDueOn)}`} />}
      </section>

      {loan.closedReason && (
        <p className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
          {loan.status === 'written_off' ? 'Written off' : 'Cancelled'} on {day(loan.closedOn)}: {loan.closedReason}
        </p>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader title="Terms" />
          <dl className="space-y-2 text-sm">
            <Term label={words.counterparty} value={`${loan.counterpartyName} (${LOAN_COUNTERPARTY_LABELS[loan.counterpartyKind]})`} />
            {loan.riderName && <Term label="Linked rider" value={loan.riderName} />}
            {loan.staffName && <Term label="Linked staff" value={loan.staffName} />}
            <Term label="Location" value={loan.branchName ?? 'Whole business'} />
            <Term label="Interest" value={loan.interestType === 'none' ? LOAN_INTEREST_TYPE_LABELS.none : `${LOAN_INTEREST_TYPE_LABELS[loan.interestType]}, ${Number(loan.interestRate)}% a year`} />
            <Term label="Repayments" value={`${loan.instalmentCount} × ${LOAN_FREQUENCY_LABELS[loan.frequency].toLowerCase()}${loan.frequency === 'custom' && loan.intervalDays ? ` (every ${loan.intervalDays} days)` : ''}`} />
            <Term label="Issued" value={day(loan.issuedOn)} />
            <Term label="First repayment" value={day(loan.firstDueOn)} />
            <Term label="Final repayment" value={day(loan.finalDueOn ?? loan.instalments.at(-1)?.dueOn)} />
            <Term label="Paid out by" value={PAYMENT_METHOD_LABELS[loan.paymentMethod as PaymentMethod] ?? loan.paymentMethod} />
            {compareAmounts(loan.processingFee, 0) > 0 && (
              <Term label="Processing fee" value={`${money(loan.processingFee, c)}, ${LOAN_FEE_COLLECTION_LABELS[loan.feeCollection].toLowerCase()}`} />
            )}
            <Term label="Early settlement" value={LOAN_EARLY_REPAYMENT_LABELS[loan.earlyRepaymentPolicy]} />
            <Term
              label="Late fee"
              value={
                loan.lateFeeType === 'none'
                  ? 'None'
                  : `${loan.lateFeeType === 'fixed' ? money(loan.lateFeeValue, c) : `${loan.lateFeeValue}% of the instalment`} after ${loan.lateFeeGraceDays} days`
              }
            />
            <Term label="Currency" value={c} />
            {loan.createdByName && <Term label="Recorded by" value={`${loan.createdByName}, ${dateTime(loan.createdAt, 'UTC')}`} />}
            {loan.attachmentId && (
              <div className="flex items-center justify-between gap-2 pt-1">
                <DocumentLink loanId={loan.id} attachmentId={loan.attachmentId} label="Supporting document" />
                {can('loans:manage') && (
                  <button type="button" className="text-xs text-slate-500 hover:text-red-700" onClick={() => setDialog('removeDoc')}>
                    Remove
                  </button>
                )}
              </div>
            )}
          </dl>
          {loan.notes && <p className="mt-3 whitespace-pre-line rounded-lg bg-slate-50 p-2 text-sm text-slate-700">{loan.notes}</p>}
        </Card>

        <Card className="lg:col-span-2" padded={false}>
          <div className="p-4 sm:p-5">
            <CardHeader
              title="Repayment schedule"
              subtitle={loan.status === 'draft' ? 'What issuing this draft would create' : 'Fixed when the loan was issued'}
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[620px] text-sm">
              <thead className="border-y border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2 font-medium">#</th>
                  <th className="px-3 py-2 font-medium">Due</th>
                  <th className="px-3 py-2 text-right font-medium">Principal</th>
                  <th className="px-3 py-2 text-right font-medium">Interest</th>
                  <th className="px-3 py-2 text-right font-medium">Fees</th>
                  <th className="px-3 py-2 text-right font-medium">Paid</th>
                  <th className="px-3 py-2 text-right font-medium">Remaining</th>
                  <th className="px-3 py-2 font-medium">State</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 tabular-nums">
                {loan.instalments.map((i) => (
                  <tr key={i.id}>
                    <td className="px-3 py-2 text-slate-500">{i.sequence}</td>
                    <td className="whitespace-nowrap px-3 py-2">{day(i.dueOn)}</td>
                    <td className="px-3 py-2 text-right">{money(i.principal, c)}</td>
                    <td className="px-3 py-2 text-right">{money(i.interest, c)}</td>
                    <td className="px-3 py-2 text-right text-slate-600">{compareAmounts(i.fee, 0) > 0 ? money(i.fee, c) : '—'}</td>
                    <td className="px-3 py-2 text-right text-slate-600">{money(addPaid(i), c)}</td>
                    <td className="px-3 py-2 text-right font-medium">{money(i.remaining, c)}</td>
                    <td className="px-3 py-2"><InstalmentState state={i.state} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {loan.status !== 'draft' && (loan.charges.length > 0 || loan.lateFeeCandidates.length > 0) && <FeesCard loan={loan} />}

      {riderLoan && loan.status !== 'draft' && (
        <DeductionAgreementCard loan={loan} onCreate={() => setDialog('agreement')} onRevoke={() => setDialog('revoke')} />
      )}

      <Card padded={false}>
        <div id="history" className="scroll-mt-20 p-4 sm:p-5">
          <CardHeader title="History" subtitle="Every entry on this loan's ledger. Corrections are new entries; nothing is deleted." />
        </div>
        {loan.transactions.length === 0 ? (
          <p className="px-5 pb-6 text-sm text-slate-500">Nothing recorded yet. A draft has no ledger until it is issued.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {loan.transactions.map((t) => (
              <TransactionRow key={t.id} loan={loan} t={t} />
            ))}
          </ul>
        )}
      </Card>

      {dialog === 'repay' && <RepaymentModal loan={loan} onClose={() => setDialog(null)} />}
      {dialog === 'settle' && <SettleModal loan={loan} onClose={() => setDialog(null)} />}
      {dialog === 'removeDoc' && <RemoveDocumentModal loan={loan} onClose={() => setDialog(null)} />}
      {dialog === 'edit' && <EditLoanModal loan={loan} onClose={() => setDialog(null)} />}
      {dialog === 'cancel' && <CloseLoanModal loan={loan} mode="cancel" onClose={() => setDialog(null)} />}
      {dialog === 'writeoff' && <CloseLoanModal loan={loan} mode="writeoff" onClose={() => setDialog(null)} />}
      {dialog === 'agreement' && <AgreementModal loan={loan} onClose={() => setDialog(null)} />}
      {dialog === 'revoke' && <RevokeAgreementModal loan={loan} onClose={() => setDialog(null)} />}
      {dialog === 'deduct' && <DeductionModal loan={loan} onClose={() => setDialog(null)} />}
      {dialog === 'topup' && <TopUpModal loan={loan} onClose={() => setDialog(null)} />}
      {dialog === 'adjust' && <AdjustModal loan={loan} onClose={() => setDialog(null)} />}
    </div>
  );
}

function addPaid(i: LoanInstalmentView): string {
  return addAmounts(i.principalPaid, i.interestPaid, i.feePaid);
}

/** Fees charged on the loan, late fees that could be applied, and waivers. */
function FeesCard({ loan }: { loan: LoanView }): ReactNode {
  const { can } = useAuth();
  const apply = useApplyLateFee(loan.id);
  const [waiving, setWaiving] = useState<string | null>(null);
  const c = loan.currencyCode;
  return (
    <Card>
      <CardHeader title="Fees" subtitle="Charged by a person, never automatically. A waiver is recorded beside the fee it forgives." />
      {loan.charges.length > 0 && (
        <ul className="divide-y divide-slate-100 text-sm">
          {loan.charges.map((ch) => (
            <li key={ch.id} className={cn('flex flex-wrap items-center justify-between gap-2 py-2', ch.waived && 'opacity-60')}>
              <span>
                {ch.kind === 'processing_fee' ? 'Processing fee' : 'Late fee'} · instalment {ch.sequence} · {day(ch.assessedOn)}
                {ch.waived && <span className="ml-2 text-xs text-slate-500">Waived: {ch.waivedReason}</span>}
              </span>
              <span className="flex items-center gap-3">
                <span className="tabular-nums">{money(ch.amount, c)}</span>
                {!ch.waived && can('loans:manage') && (loan.status === 'active' || loan.status === 'overdue') && (
                  <Button size="sm" variant="ghost" onClick={() => setWaiving(ch.id)}>Waive</Button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {loan.lateFeeCandidates.length > 0 && can('loans:manage') && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="text-sm font-medium text-amber-900">Late fees that may be applied</p>
          <ul className="mt-2 space-y-1.5">
            {loan.lateFeeCandidates.map((cand) => (
              <li key={cand.instalmentId} className="flex flex-wrap items-center justify-between gap-2 text-sm text-amber-900">
                <span>Instalment {cand.sequence}, due {day(cand.dueOn)}: {money(cand.fee, c)}</span>
                <Button
                  size="sm"
                  variant="secondary"
                  loading={apply.isPending && apply.variables?.instalmentId === cand.instalmentId}
                  onClick={() => apply.mutate({ instalmentId: cand.instalmentId, idempotencyKey: newSubmissionKey() })}
                >
                  Apply late fee
                </Button>
              </li>
            ))}
          </ul>
          {apply.error && <p role="alert" className="mt-2 text-sm text-red-700">{apply.error.message}</p>}
        </div>
      )}
      {waiving && <WaiveModal loan={loan} chargeId={waiving} onClose={() => setWaiving(null)} />}
    </Card>
  );
}

function WaiveModal({ loan, chargeId, onClose }: { loan: LoanView; chargeId: string; onClose: () => void }): ReactNode {
  const waive = useWaiveCharge(loan.id);
  const [reason, setReason] = useState('');
  return (
    <Modal open onClose={onClose} title="Waive this fee">
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); waive.mutate({ chargeId, reason: reason.trim() }, { onSuccess: onClose }); }}>
        <Textarea label="Reason" required value={reason} onChange={(e) => setReason(e.target.value)} />
        {waive.error && <p role="alert" className="text-sm text-red-700">{waive.error.message}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Keep the fee</Button>
          <Button type="submit" loading={waive.isPending} disabled={reason.trim().length < 3}>Waive</Button>
        </div>
      </form>
    </Modal>
  );
}

/** Settle in full today, at the amount the loan's own early-settlement policy gives. */
/** Exported for its test. */
export function SettleModal({ loan, onClose }: { loan: LoanView; onClose: () => void }): ReactNode {
  const { business } = useAuth();
  const today = todayIn(business?.timezone ?? 'UTC');
  const [paidOn, setPaidOn] = useState(today);
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [accountId, setAccountId] = useState('');
  const [reference, setReference] = useState('');
  const [key] = useState(newSubmissionKey);
  const quote = useSettlementQuote(loan.id, paidOn, true);
  const settle = useSettleLoan(loan.id);
  const c = loan.currencyCode;
  return (
    <Modal open onClose={onClose} title="Settle this loan early">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!quote.data) return;
          settle.mutate(
            {
              amount: quote.data.payoff, paidOn, paymentMethod: method, transactionReference: reference.trim() || null, idempotencyKey: key,
              ...(accountId ? { accountId } : {}),
            },
            { onSuccess: onClose },
          );
        }}
      >
        <p className="text-sm text-slate-600">{LOAN_EARLY_REPAYMENT_LABELS[loan.earlyRepaymentPolicy]}.</p>
        <Input label="Settlement date" type="date" value={paidOn} max={today} min={loan.issuedOn} onChange={(e) => setPaidOn(e.target.value)} />
        {quote.isPending ? (
          <Spinner label="Working out the settlement" />
        ) : quote.error ? (
          <p role="alert" className="text-sm text-red-700">{quote.error.message}</p>
        ) : (
          <dl className="grid grid-cols-2 gap-3 rounded-lg bg-slate-50 p-3 text-sm">
            <div>
              <dt className="text-xs text-slate-500">To settle in full</dt>
              <dd className="text-lg font-semibold tabular-nums text-slate-900">{money(quote.data.payoff, c)}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Interest waived</dt>
              <dd className="tabular-nums text-slate-900">{money(quote.data.interestWaived, c)}</dd>
            </div>
            {quote.data.proration && (
              <p className="col-span-2 text-xs text-slate-600" data-testid="proration">
                Instalment in progress: {quote.data.proration.daysElapsed} of {quote.data.proration.periodDays} days
                passed, so {money(quote.data.proration.interestEarned, c)} of its{' '}
                {money(quote.data.proration.interestScheduled, c)} interest is charged. Later instalments carry no
                interest.
              </p>
            )}
          </dl>
        )}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Select
            label="Payment method"
            value={method}
            onChange={(e) => { setMethod(e.target.value as PaymentMethod); setAccountId(''); }}
            options={Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => ({ value, label }))}
          />
          <Input label="Transaction reference" value={reference} onChange={(e) => setReference(e.target.value)} />
        </div>
        <AccountSelect
          method={method}
          value={accountId}
          onChange={setAccountId}
          error={settle.error instanceof ApiError ? settle.error.fieldError('accountId') : undefined}
        />
        {settle.error && <p role="alert" className="text-sm text-red-700">{settle.error.message}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={settle.isPending} disabled={!quote.data}>Settle loan</Button>
        </div>
      </form>
    </Modal>
  );
}

function RemoveDocumentModal({ loan, onClose }: { loan: LoanView; onClose: () => void }): ReactNode {
  const remove = useRemoveLoanDocument(loan.id);
  const [reason, setReason] = useState('');
  return (
    <Modal open onClose={onClose} title="Remove the supporting document">
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); remove.mutate({ reason: reason.trim() }, { onSuccess: onClose }); }}>
        <p className="text-sm text-slate-600">The file is unlinked from {loan.reference} and removed. The removal and its reason are kept in the audit trail.</p>
        <Textarea label="Reason" required value={reason} onChange={(e) => setReason(e.target.value)} />
        {remove.error && <p role="alert" className="text-sm text-red-700">{remove.error.message}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Keep it</Button>
          <Button type="submit" variant="danger" loading={remove.isPending} disabled={reason.trim().length < 3}>Remove</Button>
        </div>
      </form>
    </Modal>
  );
}

function Figure({ label, value, sub, tone = 'plain' }: { label: string; value: string; sub?: string; tone?: 'plain' | 'good' | 'owed' | 'warn' }): ReactNode {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3.5">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className={cn('mt-1 text-lg font-semibold tabular-nums', tone === 'good' && 'text-emerald-700', tone === 'owed' && 'text-rose-700', tone === 'warn' && 'text-amber-700', tone === 'plain' && 'text-slate-900')}>
        {value}
      </p>
      {sub && <p className="mt-0.5 text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

function Term({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-right text-slate-900">{value}</dd>
    </div>
  );
}

const INSTALMENT_TONES: Record<LoanInstalmentView['state'], 'neutral' | 'success' | 'warning' | 'danger' | 'info'> = {
  paid: 'success',
  partial: 'warning',
  due: 'info',
  overdue: 'danger',
  upcoming: 'neutral',
  written_off: 'warning',
};

function InstalmentState({ state }: { state: LoanInstalmentView['state'] }): ReactNode {
  const label = state === 'written_off' ? 'Written off' : state.charAt(0).toUpperCase() + state.slice(1);
  return <Badge tone={INSTALMENT_TONES[state]}>{label}</Badge>;
}

const KIND_LABEL: Record<LoanTransactionView['kind'], string> = {
  disbursement: 'Disbursement',
  repayment: 'Repayment',
  reversal: 'Reversal',
  write_off: 'Write-off',
  interest_rebate: 'Interest waived on early settlement',
  top_up: 'Additional amount lent',
  adjustment: 'Balance correction (no money moved)',
};

function TransactionRow({ loan, t }: { loan: LoanView; t: LoanTransactionView }): ReactNode {
  const { can } = useAuth();
  const [reversing, setReversing] = useState(false);
  const c = loan.currencyCode;
  const reversible =
    (t.kind === 'repayment' || t.kind === 'write_off' || t.kind === 'interest_rebate' || t.kind === 'top_up' || t.kind === 'adjustment') &&
    !t.reversedById &&
    can('loans:manage') &&
    loan.status !== 'cancelled' &&
    t.notes !== 'Processing fee, taken from the money paid out';
  const label =
    t.kind === 'disbursement'
      ? loan.direction === 'receivable'
        ? 'Loan paid out'
        : 'Loan received'
      : t.kind === 'repayment'
        ? loan.direction === 'receivable'
          ? 'Repayment received'
          : 'Paid to lender'
        : KIND_LABEL[t.kind];

  return (
    <li className={cn('flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:px-5', t.reversedById && 'opacity-60')}>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-900">
          {label}
          {t.source !== 'manual' && <span className="ml-2 text-xs font-normal text-slate-500">{LOAN_REPAYMENT_SOURCE_LABELS[t.source]}</span>}
          {t.reversedById && <Badge tone="neutral">Reversed</Badge>}
        </p>
        <p className="text-xs text-slate-500">
          {day(t.occurredOn)}
          {t.paymentMethod ? ` · ${PAYMENT_METHOD_LABELS[t.paymentMethod as PaymentMethod] ?? t.paymentMethod}` : ''}
          {t.accountName ? ` · ${t.accountName}` : ''}
          {t.transactionReference ? ` · Ref ${t.transactionReference}` : ''}
          {t.recordedByName ? ` · by ${t.recordedByName}` : ''}
        </p>
        {t.notes && <p className="mt-0.5 text-xs text-slate-600">{t.notes}</p>}
      </div>
      <div className="flex items-center justify-between gap-4 sm:justify-end">
        <div className="text-right tabular-nums">
          <p className={cn('text-sm font-semibold', t.kind === 'reversal' ? 'text-rose-700' : 'text-slate-900')}>
            {t.kind === 'reversal' ? '−' : ''}
            {money(t.amount, c)}
          </p>
          {(t.kind === 'repayment' || t.kind === 'write_off' || t.kind === 'interest_rebate') && (
            <p className="text-xs text-slate-500">
              {money(t.principal, c)} principal · {money(t.interest, c)} interest
              {compareAmounts(t.fee, 0) !== 0 ? ` · ${money(t.fee, c)} fees` : ''}
            </p>
          )}
        </div>
        {reversible && (
          <Button size="sm" variant="ghost" icon={<Undo2 className="size-4" />} onClick={() => setReversing(true)}>
            Reverse
          </Button>
        )}
      </div>
      {reversing && <ReverseModal loan={loan} t={t} onClose={() => setReversing(false)} />}
    </li>
  );
}

function ReverseModal({ loan, t, onClose }: { loan: LoanView; t: LoanTransactionView; onClose: () => void }): ReactNode {
  const reverse = useReverseTransaction(loan.id);
  const [key] = useState(newSubmissionKey);
  const [reason, setReason] = useState('');
  return (
    <Modal
      open
      onClose={onClose}
      title={`Reverse this ${t.kind === 'write_off' ? 'write-off' : t.kind === 'top_up' ? 'added amount' : t.kind === 'adjustment' ? 'correction' : 'payment'}`}
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          reverse.mutate({ transactionId: t.id, reason: reason.trim(), idempotencyKey: key }, { onSuccess: onClose });
        }}
      >
        <p className="text-sm text-slate-600">
          A reversal of {money(t.amount, loan.currencyCode)} is added to the history. The original entry stays, marked
          reversed, and the balance is restored.
        </p>
        <Textarea label="Reason" required value={reason} onChange={(e) => setReason(e.target.value)} placeholder="For example: transfer bounced" />
        {reverse.error && <p role="alert" className="text-sm text-red-700">{reverse.error.message}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="danger" loading={reverse.isPending} disabled={reason.trim().length < 3}>Reverse</Button>
        </div>
      </form>
    </Modal>
  );
}

function CloseLoanModal({ loan, mode, onClose }: { loan: LoanView; mode: 'cancel' | 'writeoff'; onClose: () => void }): ReactNode {
  const cancel = useCancelLoan(loan.id);
  const writeOff = useWriteOffLoan(loan.id);
  const action = mode === 'cancel' ? cancel : writeOff;
  const [reason, setReason] = useState('');
  return (
    <Modal open onClose={onClose} title={mode === 'cancel' ? 'Cancel this loan' : 'Write off this loan'}>
      <form
        className="space-y-4"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          action.mutate({ reason: reason.trim() }, { onSuccess: onClose });
        }}
      >
        <p className="text-sm text-slate-600">
          {mode === 'cancel'
            ? loan.status === 'draft'
              ? 'The draft is closed. Nothing was paid out, so nothing is reversed.'
              : 'For a loan entered in error, with nothing repaid. The disbursement is reversed and both entries stay in the history.'
            : `The ${money(loan.balanceOwed, loan.currencyCode)} still owed is forgiven and recorded as a write-off — not as an operating expense. It can be reversed later if the money is recovered.`}
        </p>
        <Textarea label="Reason" required value={reason} onChange={(e) => setReason(e.target.value)} />
        {action.error && <p role="alert" className="text-sm text-red-700">{action.error.message}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Keep the loan</Button>
          <Button type="submit" variant="danger" loading={action.isPending} disabled={reason.trim().length < 3}>
            {mode === 'cancel' ? 'Cancel loan' : 'Write off'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function EditLoanModal({ loan, onClose }: { loan: LoanView; onClose: () => void }): ReactNode {
  const update = useUpdateLoan(loan.id);
  const draft = loan.status === 'draft';
  const [name, setName] = useState(loan.counterpartyName);
  const [phone, setPhone] = useState(loan.counterpartyPhone ?? '');
  const [purpose, setPurpose] = useState(loan.purpose ?? '');
  const [notes, setNotes] = useState(loan.notes ?? '');
  const [principal, setPrincipal] = useState(loan.principal);
  const [rate, setRate] = useState(String(Number(loan.interestRate)));
  const [count, setCount] = useState(String(loan.instalmentCount));
  const [issuedOn, setIssuedOn] = useState(loan.issuedOn);
  const [firstDueOn, setFirstDueOn] = useState(loan.firstDueOn);
  // The account a draft is paid out of when issued; empty keeps what it has.
  const [accountId, setAccountId] = useState('');
  const err = (f: string) => (update.error instanceof ApiError ? update.error.fieldError(f) : undefined);

  return (
    <Modal open onClose={onClose} title={`Edit ${loan.reference}`}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          update.mutate(
            {
              counterpartyName: name.trim(),
              counterpartyPhone: phone.trim() || null,
              purpose: purpose.trim() || null,
              notes: notes.trim() || null,
              ...(draft
                ? {
                    principal,
                    ...(loan.interestType !== 'none' ? { interestRate: rate } : {}),
                    instalmentCount: Number(count),
                    issuedOn,
                    firstDueOn,
                    ...(accountId ? { accountId } : {}),
                  }
                : {}),
            },
            { onSuccess: onClose },
          );
        }}
      >
        {!draft && (
          <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
            An issued loan keeps the terms that were agreed. To change the amount, rate or schedule, cancel it and issue a new one.
          </p>
        )}
        <Input label="Name" required value={name} onChange={(e) => setName(e.target.value)} error={err('counterpartyName')} />
        <Input label="Phone number" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} error={err('counterpartyPhone')} />
        <Input label="Purpose" value={purpose} onChange={(e) => setPurpose(e.target.value)} />
        {draft && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input label="Principal" inputMode="decimal" value={principal} onChange={(e) => setPrincipal(e.target.value)} error={err('principal')} />
            {loan.interestType !== 'none' && (
              <Input label="Interest rate (% a year)" inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} error={err('interestRate')} />
            )}
            <Input label="Number of instalments" inputMode="numeric" value={count} onChange={(e) => setCount(e.target.value)} error={err('instalmentCount')} />
            <Input label="Loan issue date" type="date" value={issuedOn} onChange={(e) => setIssuedOn(e.target.value)} error={err('issuedOn')} />
            <Input label="First repayment date" type="date" value={firstDueOn} onChange={(e) => setFirstDueOn(e.target.value)} error={err('firstDueOn')} />
            <AccountSelect method={loan.paymentMethod} value={accountId} onChange={setAccountId} error={err('accountId')} />
          </div>
        )}
        <Textarea label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
        {update.error && !Object.keys((update.error as ApiError).fields ?? {}).length && (
          <p role="alert" className="text-sm text-red-700">{update.error.message}</p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={update.isPending}>Save</Button>
        </div>
      </form>
    </Modal>
  );
}

function DeductionAgreementCard({ loan, onCreate, onRevoke }: { loan: LoanView; onCreate: () => void; onRevoke: () => void }): ReactNode {
  const { can } = useAuth();
  const a = loan.deductionAgreement;
  const live = loan.status === 'active' || loan.status === 'overdue';
  return (
    <Card>
      <CardHeader
        title="Deductions from remittance or payout"
        subtitle="Only with the rider's written agreement. Nothing is deducted automatically."
        action={
          can('loans:manage') && live ? (
            a ? (
              <Button size="sm" variant="secondary" onClick={onRevoke}>Revoke</Button>
            ) : (
              <Button size="sm" icon={<ShieldCheck className="size-4" />} onClick={onCreate}>Record agreement</Button>
            )
          ) : undefined
        }
      />
      {a ? (
        <dl className="max-w-md space-y-2 text-sm">
          <Term label="Up to, per deduction" value={money(a.amountPerDeduction, loan.currencyCode)} />
          <Term label="From" value={a.sources.map((s) => (s === 'remittance' ? 'Remittances' : 'Payouts')).join(' and ')} />
          <Term label="Agreed" value={`${day(a.agreedOn)}${a.authorisedByName ? `, by ${a.authorisedByName}` : ''}`} />
          {a.attachmentId && <DocumentLink loanId={loan.id} attachmentId={a.attachmentId} label="Signed agreement" />}
        </dl>
      ) : (
        <p className="text-sm text-slate-500">No agreement recorded. Repayments are collected directly.</p>
      )}
      <p className="mt-3 text-xs text-slate-500">
        A deduction is a separate repayment on this loan. The remittance and the payout it comes from are left exactly as
        they were, and remittance revenue is unaffected.
      </p>
    </Card>
  );
}

function AgreementModal({ loan, onClose }: { loan: LoanView; onClose: () => void }): ReactNode {
  const { business } = useAuth();
  const create = useCreateAgreement(loan.id);
  const [amount, setAmount] = useState(loan.nextDueAmount ?? '');
  const [remittance, setRemittance] = useState(true);
  const [payout, setPayout] = useState(false);
  const [agreedOn, setAgreedOn] = useState(todayIn(business?.timezone ?? 'UTC'));
  const [notes, setNotes] = useState('');
  const parsed = moneySchema.safeParse(amount);
  const sources: LoanDeductionSource[] = [...(remittance ? (['remittance'] as const) : []), ...(payout ? (['payout'] as const) : [])];

  return (
    <Modal open onClose={onClose} title="Record a deduction agreement">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!parsed.success) return;
          create.mutate({ amountPerDeduction: parsed.data, sources, agreedOn, notes: notes.trim() || null }, { onSuccess: onClose });
        }}
      >
        <p className="text-sm text-slate-600">
          Record only what {loan.counterpartyName} agreed to in writing. Each deduction still has to be recorded by hand,
          and cannot exceed the amount below.
        </p>
        <Input label={`Most per deduction (${loan.currencyCode})`} inputMode="decimal" required value={amount} onChange={(e) => setAmount(e.target.value)} />
        <fieldset>
          <legend className="label-base">May be deducted from</legend>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={remittance} onChange={(e) => setRemittance(e.target.checked)} /> Weekly remittances</label>
          <label className="mt-1 flex items-center gap-2 text-sm"><input type="checkbox" checked={payout} onChange={(e) => setPayout(e.target.checked)} /> Rider payouts</label>
        </fieldset>
        <Input label="Agreed on" type="date" required value={agreedOn} onChange={(e) => setAgreedOn(e.target.value)} />
        <Textarea label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
        {create.error && <p role="alert" className="text-sm text-red-700">{create.error.message}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={create.isPending} disabled={!parsed.success || sources.length === 0}>Record agreement</Button>
        </div>
      </form>
    </Modal>
  );
}

function RevokeAgreementModal({ loan, onClose }: { loan: LoanView; onClose: () => void }): ReactNode {
  const revoke = useRevokeAgreement(loan.id);
  const [reason, setReason] = useState('');
  return (
    <Modal open onClose={onClose} title="Revoke the deduction agreement">
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); revoke.mutate({ reason: reason.trim() }, { onSuccess: onClose }); }}>
        <p className="text-sm text-slate-600">No further deductions can be recorded. Deductions already made stay on the loan.</p>
        <Textarea label="Reason" required value={reason} onChange={(e) => setReason(e.target.value)} />
        {revoke.error && <p role="alert" className="text-sm text-red-700">{revoke.error.message}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Keep it</Button>
          <Button type="submit" variant="danger" loading={revoke.isPending} disabled={reason.trim().length < 3}>Revoke</Button>
        </div>
      </form>
    </Modal>
  );
}

function DeductionModal({ loan, onClose }: { loan: LoanView; onClose: () => void }): ReactNode {
  const sources = useDeductionSources(loan.id, true);
  const record = useRecordDeduction(loan.id);
  const [key] = useState(newSubmissionKey);
  const agreement = loan.deductionAgreement!;
  const allowed = (sources.data ?? []).filter((s) => agreement.sources.includes(s.source) && !s.deducted);
  const [sourceId, setSourceId] = useState('');
  const [amount, setAmount] = useState(
    compareAmounts(agreement.amountPerDeduction, loan.balanceOwed) > 0 ? loan.balanceOwed : agreement.amountPerDeduction,
  );
  const chosen = allowed.find((s) => s.id === sourceId);
  const parsed = moneySchema.safeParse(amount);
  const tooMuch = parsed.success && compareAmounts(parsed.data, agreement.amountPerDeduction) > 0;

  return (
    <Modal open onClose={onClose} title="Record a deduction">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!chosen || !parsed.success) return;
          record.mutate({ source: chosen.source, sourceId: chosen.id, amount: parsed.data, idempotencyKey: key }, { onSuccess: onClose });
        }}
      >
        {sources.isPending ? (
          <Spinner label="Loading remittances and payouts" />
        ) : allowed.length === 0 ? (
          <p className="text-sm text-slate-600">
            There is no remittance payment or payout since this loan was issued that has not already had a deduction.
          </p>
        ) : (
          <Select
            label="Deduct from"
            value={sourceId}
            onChange={(e) => setSourceId(e.target.value)}
            placeholder="Choose a remittance or payout"
            options={allowed.map((s) => ({ value: s.id, label: `${s.label} — ${money(s.amount, loan.currencyCode)}` }))}
          />
        )}
        <Input
          label={`Amount (${loan.currencyCode})`}
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          error={tooMuch ? `The agreement allows at most ${money(agreement.amountPerDeduction, loan.currencyCode)}` : undefined}
        />
        {record.error && <p role="alert" className="text-sm text-red-700">{record.error.message}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={record.isPending} disabled={!chosen || !parsed.success || tooMuch}>Record deduction</Button>
        </div>
      </form>
    </Modal>
  );
}

function DocumentLink({ loanId, attachmentId, label }: { loanId: string; attachmentId: string; label: string }): ReactNode {
  const path = useBusinessPath();
  const [error, setError] = useState<string | null>(null);
  return (
    <span>
      <button
        type="button"
        className="inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:underline"
        onClick={async () => {
          setError(null);
          try {
            const blob = await fetchBlob(`/api/v1${path(`/loans/${loanId}/documents/${attachmentId}`)}`);
            window.open(URL.createObjectURL(blob), '_blank', 'noopener');
          } catch (e) {
            setError((e as Error).message);
          }
        }}
      >
        <FileText className="size-4" aria-hidden /> {label}
      </button>
      {error && <span className="ml-2 text-xs text-red-600">{error}</span>}
    </span>
  );
}

/**
 * Lend more on this loan.
 *
 * The original amount and everything recorded so far stay as they are; the
 * added amount is its own entry, paid out through its own account, and is
 * due back on its own date, interest-free. "Credit" records an amount owed
 * without money moving now — for a principal keyed too low.
 */
function TopUpModal({ loan, onClose }: { loan: LoanView; onClose: () => void }): ReactNode {
  const { business } = useAuth();
  const today = todayIn(business?.timezone ?? 'UTC');
  const topUp = useTopUpLoan(loan.id);
  const [key] = useState(newSubmissionKey);
  const [amount, setAmount] = useState('');
  const [occurredOn, setOccurredOn] = useState(today);
  const [dueOn, setDueOn] = useState(loan.nextDueOn ?? today);
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [accountId, setAccountId] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const fieldError = (f: string) => (topUp.error instanceof ApiError ? topUp.error.fieldError(f) : undefined);

  return (
    <Modal open onClose={onClose} title={loan.direction === 'receivable' ? 'Add to this loan' : 'Add to this borrowing'}>
      <form
        className="space-y-4"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          topUp.mutate(
            {
              amount,
              occurredOn,
              dueOn,
              paymentMethod: method,
              accountId: accountId || null,
              transactionReference: reference.trim() || null,
              notes: notes.trim(),
              idempotencyKey: key,
            },
            { onSuccess: onClose },
          );
        }}
      >
        <p className="text-sm text-slate-600">
          Originally {money(loan.principal, loan.currencyCode)}
          {compareAmounts(loan.additionalAmounts, 0) > 0 ? `, plus ${money(loan.additionalAmounts, loan.currencyCode)} added since` : ''}.
          The amount added here is repaid as its own instalment, with no interest.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input label={`Amount (${loan.currencyCode})`} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} error={fieldError('amount')} />
          <Input label="Paid out on" type="date" max={today} value={occurredOn} onChange={(e) => setOccurredOn(e.target.value)} error={fieldError('occurredOn')} />
          <Input label="Due back on" type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} error={fieldError('dueOn')} />
          <Select
            label="How it was paid out"
            value={method}
            onChange={(e) => {
              setMethod(e.target.value as PaymentMethod);
              setAccountId('');
            }}
            options={PAYMENT_METHODS.map((m) => ({ value: m, label: m === 'credit' ? 'No money moved (correction)' : PAYMENT_METHOD_LABELS[m] }))}
          />
        </div>
        <AccountSelect method={method} value={accountId} onChange={setAccountId} error={fieldError('accountId')} />
        <Input label="Reference" value={reference} onChange={(e) => setReference(e.target.value)} />
        <Textarea label="Why more is being lent" required value={notes} onChange={(e) => setNotes(e.target.value)} />
        {topUp.error && <p role="alert" className="text-sm text-red-700">{topUp.error.message}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={topUp.isPending} disabled={!amount || notes.trim().length < 3}>
            Add amount
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Correct a balance that was entered wrong.
 *
 * Reduces what is owed without money moving, and is labelled a correction
 * everywhere — not a repayment, not a write-off. To undo a payment recorded
 * in error, reverse the payment instead.
 */
function AdjustModal({ loan, onClose }: { loan: LoanView; onClose: () => void }): ReactNode {
  const { business } = useAuth();
  const today = todayIn(business?.timezone ?? 'UTC');
  const adjust = useAdjustLoan(loan.id);
  const [key] = useState(newSubmissionKey);
  const [amount, setAmount] = useState('');
  const [occurredOn, setOccurredOn] = useState(today);
  const [reason, setReason] = useState('');
  const fieldError = (f: string) => (adjust.error instanceof ApiError ? adjust.error.fieldError(f) : undefined);

  return (
    <Modal open onClose={onClose} title="Correct the balance">
      <form
        className="space-y-4"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          adjust.mutate({ amount, occurredOn, reason: reason.trim(), idempotencyKey: key }, { onSuccess: onClose });
        }}
      >
        <p className="text-sm text-slate-600">
          Reduces the {money(loan.outstanding, loan.currencyCode)} of principal still owed, for an amount that was entered
          wrong. No cash or bank balance changes. To undo a payment, reverse it from the history instead.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input label={`Reduce by (${loan.currencyCode})`} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} error={fieldError('amount')} />
          <Input label="Date" type="date" max={today} value={occurredOn} onChange={(e) => setOccurredOn(e.target.value)} error={fieldError('occurredOn')} />
        </div>
        <Textarea label="What was wrong" required value={reason} onChange={(e) => setReason(e.target.value)} placeholder="For example: keyed ₦60,000 for ₦6,000" />
        {adjust.error && <p role="alert" className="text-sm text-red-700">{adjust.error.message}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="danger" loading={adjust.isPending} disabled={!amount || reason.trim().length < 3}>
            Correct balance
          </Button>
        </div>
      </form>
    </Modal>
  );
}
